'use client'

import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { MdArrowBack, MdSave, MdLocalHospital } from 'react-icons/md'
import { useAuth } from '@/store/hooks'
import {
    createTriage,
    updateTriage,
    getTriage,
    CreateTriageData,
    UpdateTriageData,
    TriageUrgency,
    URGENCY_LABELS,
    URGENCY_COLORS,
} from '@/lib/api/triages/triage-api'
import { ClientSelector } from '@/components/client/ClientSelector'
import { ProfessionalSearchSelector } from '@/components/ProfessionalSearchSelector'

interface FormData {
    urgency: TriageUrgency
    chiefComplaint: string
    symptoms: string
    bloodPressureSystolic: string
    bloodPressureDiastolic: string
    heartRate: string
    respiratoryRate: string
    temperature: string
    oxygenSaturation: string
    painLevel: string
    weight: string
    height: string
    allergies: string
    medications: string
    medicalHistory: string
    observations: string
    clientId: string
    professionalId: string
}

export default function TriageFormPage() {
    const { user, isProfessional } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEditing = !!id

    const [loading, setLoading] = useState(false)
    const [loadingData, setLoadingData] = useState(false)

    const [formData, setFormData] = useState<FormData>({
        urgency: 'GREEN',
        chiefComplaint: '',
        symptoms: '',
        bloodPressureSystolic: '',
        bloodPressureDiastolic: '',
        heartRate: '',
        respiratoryRate: '',
        temperature: '',
        oxygenSaturation: '',
        painLevel: '',
        weight: '',
        height: '',
        allergies: '',
        medications: '',
        medicalHistory: '',
        observations: '',
        clientId: '',
        professionalId: '',
    })

    useEffect(() => {
        if (user?.companyId && isEditing && id) {
            loadTriage()
        }
    }, [id, isEditing, user?.companyId])

    // Quem está logado como profissional é o profissional da triagem: não há o
    // que escolher, e o seletor fica escondido (ver o formulário abaixo).
    useEffect(() => {
        if (isEditing || !isProfessional || !user?.professionalId) return
        setFormData(prev =>
            prev.professionalId
                ? prev
                : { ...prev, professionalId: String(user.professionalId) }
        )
    }, [isEditing, isProfessional, user?.professionalId])

    const loadTriage = async () => {
        if (!id) return

        setLoadingData(true)
        try {
            const response = await getTriage(parseInt(id, 10))
            if (response.success && response.result) {
                const triage = response.result
                setFormData({
                    urgency: triage.urgency || 'GREEN',
                    chiefComplaint: triage.chiefComplaint || '',
                    symptoms: triage.symptoms || '',
                    bloodPressureSystolic:
                        triage.bloodPressureSystolic?.toString() || '',
                    bloodPressureDiastolic:
                        triage.bloodPressureDiastolic?.toString() || '',
                    heartRate: triage.heartRate?.toString() || '',
                    respiratoryRate: triage.respiratoryRate?.toString() || '',
                    temperature: triage.temperature?.toString() || '',
                    oxygenSaturation: triage.oxygenSaturation?.toString() || '',
                    painLevel: triage.painLevel?.toString() || '',
                    weight: triage.weight?.toString() || '',
                    height: triage.height?.toString() || '',
                    allergies: triage.allergies || '',
                    medications: triage.medications || '',
                    medicalHistory: triage.medicalHistory || '',
                    observations: triage.observations || '',
                    clientId: triage.clientId?.toString() || '',
                    professionalId: triage.professionalId?.toString() || '',
                })
            } else {
                toast.error(response.error || 'Erro ao carregar triagem')
                navigate('/bookings/triage')
            }
        } catch (error) {
            toast.error('Erro ao carregar triagem')
            navigate('/bookings/triage')
        } finally {
            setLoadingData(false)
        }
    }

    const updateFormField = (field: keyof FormData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }))
    }

    const validateForm = (): boolean => {
        if (!formData.clientId) {
            toast.error('Selecione um paciente')
            return false
        }
        if (!formData.professionalId) {
            toast.error('Selecione um profissional')
            return false
        }
        return true
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!validateForm()) return
        if (!user?.companyId) {
            toast.error('Usuário não autenticado')
            return
        }

        setLoading(true)
        try {
            if (isEditing && id) {
                const updateData: UpdateTriageData = {
                    urgency: formData.urgency,
                    chiefComplaint: formData.chiefComplaint || undefined,
                    symptoms: formData.symptoms || undefined,
                    bloodPressureSystolic: formData.bloodPressureSystolic
                        ? parseInt(formData.bloodPressureSystolic, 10)
                        : undefined,
                    bloodPressureDiastolic: formData.bloodPressureDiastolic
                        ? parseInt(formData.bloodPressureDiastolic, 10)
                        : undefined,
                    heartRate: formData.heartRate
                        ? parseInt(formData.heartRate, 10)
                        : undefined,
                    respiratoryRate: formData.respiratoryRate
                        ? parseInt(formData.respiratoryRate, 10)
                        : undefined,
                    temperature: formData.temperature
                        ? parseFloat(formData.temperature)
                        : undefined,
                    oxygenSaturation: formData.oxygenSaturation
                        ? parseInt(formData.oxygenSaturation, 10)
                        : undefined,
                    painLevel: formData.painLevel
                        ? parseInt(formData.painLevel, 10)
                        : undefined,
                    weight: formData.weight
                        ? parseFloat(formData.weight)
                        : undefined,
                    height: formData.height
                        ? parseFloat(formData.height)
                        : undefined,
                    allergies: formData.allergies || undefined,
                    medications: formData.medications || undefined,
                    medicalHistory: formData.medicalHistory || undefined,
                    observations: formData.observations || undefined,
                }

                const response = await updateTriage(
                    parseInt(id, 10),
                    updateData
                )
                if (response.success) {
                    toast.success('Triagem atualizada com sucesso!')
                    navigate(`/bookings/triage/${id}`)
                } else {
                    toast.error(response.error || 'Erro ao atualizar triagem')
                }
            } else {
                const createData: CreateTriageData = {
                    urgency: formData.urgency,
                    chiefComplaint: formData.chiefComplaint || undefined,
                    symptoms: formData.symptoms || undefined,
                    bloodPressureSystolic: formData.bloodPressureSystolic
                        ? parseInt(formData.bloodPressureSystolic, 10)
                        : undefined,
                    bloodPressureDiastolic: formData.bloodPressureDiastolic
                        ? parseInt(formData.bloodPressureDiastolic, 10)
                        : undefined,
                    heartRate: formData.heartRate
                        ? parseInt(formData.heartRate, 10)
                        : undefined,
                    respiratoryRate: formData.respiratoryRate
                        ? parseInt(formData.respiratoryRate, 10)
                        : undefined,
                    temperature: formData.temperature
                        ? parseFloat(formData.temperature)
                        : undefined,
                    oxygenSaturation: formData.oxygenSaturation
                        ? parseInt(formData.oxygenSaturation, 10)
                        : undefined,
                    painLevel: formData.painLevel
                        ? parseInt(formData.painLevel, 10)
                        : undefined,
                    weight: formData.weight
                        ? parseFloat(formData.weight)
                        : undefined,
                    height: formData.height
                        ? parseFloat(formData.height)
                        : undefined,
                    allergies: formData.allergies || undefined,
                    medications: formData.medications || undefined,
                    medicalHistory: formData.medicalHistory || undefined,
                    observations: formData.observations || undefined,
                    clientId: parseInt(formData.clientId, 10),
                    professionalId: parseInt(formData.professionalId, 10),
                    companyId: user.companyId,
                }

                const response = await createTriage(createData)
                if (response.success && response.result) {
                    toast.success('Triagem criada com sucesso!')
                    navigate(`/bookings/triage/${response.result.id}`)
                } else {
                    toast.error(response.error || 'Erro ao criar triagem')
                }
            }
        } catch (error) {
            toast.error('Erro ao salvar triagem')
        } finally {
            setLoading(false)
        }
    }

    if (loadingData) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando dados da triagem...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-4 max-w-4xl mx-auto">
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate('/bookings/triage')}
                    className="btn btn-ghost btn-circle btn-sm"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-lg">
                        <MdLocalHospital className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            {isEditing ? 'Editar Triagem' : 'Nova Triagem'}
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {isEditing
                                ? 'Atualize as informacoes da triagem'
                                : 'Preencha os dados da nova triagem'}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">
                            Informações Gerais
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Paciente{' '}
                                        <span className="text-error">*</span>
                                    </span>
                                </label>
                                <ClientSelector
                                    value={formData.clientId ? Number(formData.clientId) : null}
                                    onChange={(id) =>
                                        updateFormField(
                                            'clientId',
                                            id ? String(id) : ''
                                        )
                                    }
                                    label=""
                                    placeholder="Selecione..."
                                    disabled={isEditing}
                                />
                            </div>

                            {!isProfessional && (
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Profissional{' '}
                                            <span className="text-error">*</span>
                                        </span>
                                    </label>
                                    <ProfessionalSearchSelector
                                        value={formData.professionalId ? Number(formData.professionalId) : null}
                                        onChange={(id) =>
                                            updateFormField(
                                                'professionalId',
                                                id ? String(id) : ''
                                            )
                                        }
                                        label=""
                                        placeholder="Selecione..."
                                        disabled={isEditing}
                                    />
                                </div>
                            )}
                        </div>

                        <div className="form-control w-full mt-4">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Urgência
                                </span>
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {(
                                    Object.keys(URGENCY_LABELS) as TriageUrgency[]
                                ).map(urgency => (
                                    <button
                                        key={urgency}
                                        type="button"
                                        onClick={() =>
                                            updateFormField('urgency', urgency)
                                        }
                                        className={`btn btn-sm gap-2 ${formData.urgency === urgency
                                            ? 'btn-secondary'
                                            : 'btn-outline'
                                            }`}
                                    >
                                        <span
                                            className={`w-3 h-3 rounded-full ${URGENCY_COLORS[urgency]}`}
                                        ></span>
                                        {URGENCY_LABELS[urgency]}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="form-control w-full mt-4">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Queixa Principal
                                </span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered w-full"
                                placeholder="Descreva a queixa principal..."
                                value={formData.chiefComplaint}
                                onChange={e =>
                                    updateFormField(
                                        'chiefComplaint',
                                        e.target.value
                                    )
                                }
                                rows={3}
                            />
                        </div>

                        <div className="form-control w-full mt-4">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Sintomas
                                </span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered w-full"
                                placeholder="Descreva os sintomas..."
                                value={formData.symptoms}
                                onChange={e =>
                                    updateFormField('symptoms', e.target.value)
                                }
                                rows={3}
                            />
                        </div>
                    </div>
                </div>

                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">Sinais Vitais</h2>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        PA Sistólica
                                    </span>
                                </label>
                                <input
                                    type="number"
                                    className="input input-bordered w-full"
                                    placeholder="mmHg"
                                    value={formData.bloodPressureSystolic}
                                    onChange={e =>
                                        updateFormField(
                                            'bloodPressureSystolic',
                                            e.target.value
                                        )
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        PA Diastólica
                                    </span>
                                </label>
                                <input
                                    type="number"
                                    className="input input-bordered w-full"
                                    placeholder="mmHg"
                                    value={formData.bloodPressureDiastolic}
                                    onChange={e =>
                                        updateFormField(
                                            'bloodPressureDiastolic',
                                            e.target.value
                                        )
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Freq. Cardíaca
                                    </span>
                                </label>
                                <input
                                    type="number"
                                    className="input input-bordered w-full"
                                    placeholder="bpm"
                                    value={formData.heartRate}
                                    onChange={e =>
                                        updateFormField(
                                            'heartRate',
                                            e.target.value
                                        )
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Freq. Respiratória
                                    </span>
                                </label>
                                <input
                                    type="number"
                                    className="input input-bordered w-full"
                                    placeholder="rpm"
                                    value={formData.respiratoryRate}
                                    onChange={e =>
                                        updateFormField(
                                            'respiratoryRate',
                                            e.target.value
                                        )
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Temperatura (C)
                                    </span>
                                </label>
                                <input
                                    type="number"
                                    step="0.1"
                                    className="input input-bordered w-full"
                                    placeholder="C"
                                    value={formData.temperature}
                                    onChange={e =>
                                        updateFormField(
                                            'temperature',
                                            e.target.value
                                        )
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Saturação O2
                                    </span>
                                </label>
                                <input
                                    type="number"
                                    className="input input-bordered w-full"
                                    placeholder="%"
                                    value={formData.oxygenSaturation}
                                    onChange={e =>
                                        updateFormField(
                                            'oxygenSaturation',
                                            e.target.value
                                        )
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Nível de Dor
                                    </span>
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    max="10"
                                    className="input input-bordered w-full"
                                    placeholder="0-10"
                                    value={formData.painLevel}
                                    onChange={e =>
                                        updateFormField(
                                            'painLevel',
                                            e.target.value
                                        )
                                    }
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mt-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Peso (kg)
                                    </span>
                                </label>
                                <input
                                    type="number"
                                    step="0.1"
                                    className="input input-bordered w-full"
                                    placeholder="kg"
                                    value={formData.weight}
                                    onChange={e =>
                                        updateFormField(
                                            'weight',
                                            e.target.value
                                        )
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Altura (cm)
                                    </span>
                                </label>
                                <input
                                    type="number"
                                    step="0.1"
                                    className="input input-bordered w-full"
                                    placeholder="cm"
                                    value={formData.height}
                                    onChange={e =>
                                        updateFormField(
                                            'height',
                                            e.target.value
                                        )
                                    }
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">
                            Histórico Médico
                        </h2>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Alergias
                                </span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered w-full"
                                placeholder="Liste as alergias conhecidas..."
                                value={formData.allergies}
                                onChange={e =>
                                    updateFormField('allergies', e.target.value)
                                }
                                rows={2}
                            />
                        </div>

                        <div className="form-control w-full mt-4">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Medicamentos em Uso
                                </span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered w-full"
                                placeholder="Liste os medicamentos em uso..."
                                value={formData.medications}
                                onChange={e =>
                                    updateFormField(
                                        'medications',
                                        e.target.value
                                    )
                                }
                                rows={2}
                            />
                        </div>

                        <div className="form-control w-full mt-4">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Histórico Médico
                                </span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered w-full"
                                placeholder="Descreva o histórico médico relevante..."
                                value={formData.medicalHistory}
                                onChange={e =>
                                    updateFormField(
                                        'medicalHistory',
                                        e.target.value
                                    )
                                }
                                rows={3}
                            />
                        </div>

                        <div className="form-control w-full mt-4">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Observações
                                </span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered w-full"
                                placeholder="Observações adicionais..."
                                value={formData.observations}
                                onChange={e =>
                                    updateFormField(
                                        'observations',
                                        e.target.value
                                    )
                                }
                                rows={3}
                            />
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={() => navigate('/bookings/triage')}
                        className="btn btn-ghost"
                        disabled={loading}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <span className="loading loading-spinner loading-sm"></span>
                                Salvando...
                            </>
                        ) : (
                            <>
                                <MdSave className="text-lg" />
                                Salvar
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    )
}
